import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import clamp from 'lodash/clamp';
import debounce from 'lodash/debounce';
import RXService from '@Services/RxService';
import HeatmapNavigatorImageService from '@Pages/Heatmap/HeatmapNavigatorDialog/HeatmapNavigatorImageService';
import { HEATMAP_OBSERVABLE_TOPICS, HEATMAP_VIEW_PARAMS } from '@Pages/Heatmap/constants';
import { ResizeMatrixSubject, ScrollMatrixSubject } from '@Pages/Heatmap/HeatmapObservableSubjects';
import { initImageRequest } from '@Containers/ImageRequest/ImageRequestService';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';

import './HeatmapNavigator.scss';

const MIN_POINTER_SIZE = 10;

function getOffset(element) {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
  };
}

function setPreviewPointerSize(el, { width, height, thumbnailUrl }) {
  if (!el) return;

  // eslint-disable-next-line no-param-reassign
  el.style.width = `${width}px`;
  // eslint-disable-next-line no-param-reassign
  el.style.height = `${height}px`;
  // eslint-disable-next-line no-param-reassign
  el.style.backgroundImage = `url(${thumbnailUrl})`;
}

function checkPreviewPointerBoundaries(pointerEl, navigatorEl, { previewX, previewY }) {
  if (!pointerEl || !navigatorEl) return { previewX, previewY };

  const { offsetWidth: pointerWidth, offsetHeight: pointerHeight } = pointerEl;
  const { offsetWidth: navigatorWidth, offsetHeight: navigatorHeight } = navigatorEl;

  return {
    previewX: clamp(previewX, 0, navigatorWidth - pointerWidth),
    previewY: clamp(previewY, 0, navigatorHeight - pointerHeight),
  };
}

function setPreviewPointerPosition(el, { previewX, previewY }) {
  if (el) {
    // eslint-disable-next-line no-param-reassign
    el.style.top = `${previewY}px`;
    // eslint-disable-next-line no-param-reassign
    el.style.left = `${previewX}px`;
    // eslint-disable-next-line no-param-reassign
    el.style.backgroundPosition = `-${previewX}px -${previewY}px`;
  }
}

function moveHeatmap(pointerEl, navigatorEl, { previewX, previewY }, { viewportWidth, viewportHeight, totalWidth, totalHeight }) {
  const { offsetHeight: pointerHeight, offsetWidth: pointerWidth } = pointerEl;
  const { offsetHeight: navigatorHeight, offsetWidth: navigatorWidth } = navigatorEl;

  const isBottomReached = previewY + pointerHeight === navigatorHeight;
  const isRightReached = previewX + pointerWidth === navigatorWidth;

  let matrixLeft;
  let matrixTop;

  if (isRightReached) {
    matrixLeft = totalWidth - viewportWidth;
  } else {
    matrixLeft = Math.round((previewX * totalWidth) / navigatorWidth);
  }

  if (isBottomReached) {
    matrixTop = totalHeight - viewportHeight;
  } else {
    matrixTop = Math.round((previewY * totalHeight) / navigatorHeight);
  }

  RXService.next(HEATMAP_OBSERVABLE_TOPICS.movedNavigator, matrixLeft, matrixTop);
}

const debouncedMoveHandler = debounce(moveHeatmap, HEATMAP_VIEW_PARAMS.navigatorFiredDebounce);

function HeatmapNavigator({ onClose, matrixName }) {
  const innerThumbnailRef = useRef();
  const previewPointerRef = useRef();
  const matrixSize = useRef();
  const pointerMouseMoveCoordinates = useRef({
    startX: undefined,
    startY: undefined,
  });
  const [thumbnailUrl, setThumbnailUrl] = useState();
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);

  const initThumbnail = (_matrixName) =>
    HeatmapNavigatorImageService.getThumbnail(_matrixName)
      .then((url) => {
        setThumbnailUrl(url);
        return initImageRequest({ url });
      })
      .then(() => {
        setThumbnailLoaded(true);
      })
      .catch((e) => {
        Logger.error(e);
        onClose();
      });

  const onMatrixResized = useCallback(
    (sizeParams) => {
      matrixSize.current = sizeParams;

      const { viewportWidth, totalWidth, viewportHeight, totalHeight } = matrixSize.current;
      const { offsetWidth: navigatorWidth, offsetHeight: navigatorHeight } = innerThumbnailRef.current;

      let pointerWidth = Math.round((viewportWidth * navigatorWidth) / totalWidth);
      let pointerHeight = Math.round((viewportHeight * navigatorHeight) / totalHeight);

      pointerWidth = Math.max(pointerWidth, MIN_POINTER_SIZE);
      pointerHeight = Math.max(pointerHeight, MIN_POINTER_SIZE);

      setPreviewPointerSize(previewPointerRef.current, { width: pointerWidth, height: pointerHeight, thumbnailUrl });
    },
    [thumbnailUrl]
  );

  const onMatrixScrolled = useCallback(({ scrollTop, scrollLeft }) => {
    if (!matrixSize.current) return;

    const { totalWidth, totalHeight } = matrixSize.current;
    const { offsetWidth: navigatorWidth, offsetHeight: navigatorHeight } = innerThumbnailRef.current;

    let previewCoordinates = {
      previewX: Math.round((scrollLeft * navigatorWidth) / totalWidth),
      previewY: Math.round((scrollTop * navigatorHeight) / totalHeight),
    };

    previewCoordinates = checkPreviewPointerBoundaries(previewPointerRef.current, innerThumbnailRef.current, previewCoordinates);

    setPreviewPointerPosition(previewPointerRef.current, previewCoordinates);
  }, []);

  useEffect(() => {
    setThumbnailUrl(undefined);
    setThumbnailLoaded(false);
    const thumbnailPromise = initThumbnail(matrixName);
    return () => {
      if (thumbnailPromise.cancel) {
        thumbnailPromise.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixName]);

  useEffect(() => {
    let rxSubscriptions = [];
    if (thumbnailLoaded) {
      rxSubscriptions = [
        ResizeMatrixSubject.subscribe({ next: onMatrixResized }),
        ScrollMatrixSubject.subscribe({ next: onMatrixScrolled }),
      ];
    }

    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [thumbnailLoaded, onMatrixResized, onMatrixScrolled]);

  /* Navigator mouse events */

  const onNavigatorMouseDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const { top: thumbnailOffsetTop, left: thumbnailOffsetLeft } = getOffset(innerThumbnailRef.current);
    const { offsetWidth: pointerWidth, offsetHeight: pointerHeight } = previewPointerRef.current;

    const centerX = event.clientX - thumbnailOffsetLeft;
    const centerY = event.clientY - thumbnailOffsetTop;

    let previewCoordinates = {
      previewX: centerX - Math.round(pointerWidth / 2),
      previewY: centerY - Math.round(pointerHeight / 2),
    };

    previewCoordinates = checkPreviewPointerBoundaries(previewPointerRef.current, innerThumbnailRef.current, previewCoordinates);

    setPreviewPointerPosition(previewPointerRef.current, previewCoordinates);
    debouncedMoveHandler(previewPointerRef.current, innerThumbnailRef.current, previewCoordinates, matrixSize.current);
  }, []);

  const onPointerMouseMove = useCallback((event) => {
    let previewCoordinates = {
      previewX: event.screenX - pointerMouseMoveCoordinates.current.startX,
      previewY: event.screenY - pointerMouseMoveCoordinates.current.startY,
    };

    previewCoordinates = checkPreviewPointerBoundaries(previewPointerRef.current, innerThumbnailRef.current, previewCoordinates);
    setPreviewPointerPosition(previewPointerRef.current, previewCoordinates);
    moveHeatmap(previewPointerRef.current, innerThumbnailRef.current, previewCoordinates, matrixSize.current);
  }, []);

  const onPointerMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', onPointerMouseMove);
    document.removeEventListener('mouseup', onPointerMouseUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerMouseDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const { offsetLeft: previewX, offsetTop: previewY } = previewPointerRef.current;

    pointerMouseMoveCoordinates.current = {
      startX: event.screenX - previewX,
      startY: event.screenY - previewY,
    };

    document.addEventListener('mousemove', onPointerMouseMove);
    document.addEventListener('mouseup', onPointerMouseUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const innerThumbnailEl = innerThumbnailRef.current;
    const previewPointerEl = previewPointerRef.current;

    if (thumbnailLoaded) {
      innerThumbnailEl.addEventListener('mousedown', onNavigatorMouseDown);
      previewPointerEl.addEventListener('mousedown', onPointerMouseDown);
    }

    return () => {
      if (thumbnailLoaded) {
        innerThumbnailEl.removeEventListener('mousedown', onNavigatorMouseDown);
        previewPointerEl.removeEventListener('mousedown', onPointerMouseDown);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbnailLoaded]);

  useEffect(
    () => () => {
      document.removeEventListener('mousemove', onPointerMouseMove);
      document.removeEventListener('mouseup', onPointerMouseUp);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="heatmap-navigator">
      {thumbnailUrl ? (
        <div className="heatmap-navigator__inner-thumbnail" ref={innerThumbnailRef}>
          <img className="heatmap-navigator__thumbnail-image" alt="Heatmap navigator" src={thumbnailUrl} />
          <div className="heatmap-navigator__preview-pointer" ref={previewPointerRef} />
        </div>
      ) : (
        <SimpleSpinner />
      )}
    </div>
  );
}

HeatmapNavigator.propTypes = {
  onClose: PropTypes.func.isRequired,
  matrixName: PropTypes.string,
};

export default HeatmapNavigator;
